import React, { ReactNode, useState } from 'react';
import {
  DndContext,
  CollisionDetection,
  closestCenter,
  pointerWithin,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  DragStartEvent,
  DragEndEvent,
  DragOverEvent,
  useDraggable,
  useDroppable,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';

interface StringItemProps {
  value: string;
  onAdd?: () => void;
  onRemove?: () => void;
  isDragging?: boolean;
  showDragHandle?: boolean;
  listeners?: Record<string, unknown>;
  rightContent?: ReactNode;
}

function StringItem({
  value,
  onAdd,
  onRemove,
  isDragging,
  showDragHandle,
  listeners,
  rightContent,
}: StringItemProps) {
  return (
    <Paper
      elevation={isDragging ? 4 : 1}
      sx={{
        p: 1.5,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        opacity: isDragging ? 0.8 : 1,
        bgcolor: isDragging ? 'action.hover' : 'background.paper',
      }}
    >
      {showDragHandle && (
        <Box {...listeners} sx={{ cursor: 'grab', display: 'flex', alignItems: 'center' }}>
          <DragIndicatorIcon color="action" />
        </Box>
      )}
      <Typography sx={{ flex: 1 }}>{value}</Typography>
      {rightContent}
      {onAdd && (
        <IconButton size="small" onClick={onAdd} color="primary">
          <AddIcon />
        </IconButton>
      )}
      {onRemove && (
        <IconButton size="small" onClick={onRemove} color="error">
          <CloseIcon />
        </IconButton>
      )}
    </Paper>
  );
}

function DropPlaceholder({ label }: { label: string }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        border: '2px dashed',
        borderColor: 'primary.main',
        bgcolor: 'primary.100',
        color: 'primary.dark',
      }}
    >
      <Typography>{label}</Typography>
    </Paper>
  );
}

interface SortableStringItemProps {
  id: string;
  value: string;
  onRemove: () => void;
  rightContent?: ReactNode;
}

const AVAILABLE_ITEM_PREFIX = 'available:';
const SELECTED_DROPPABLE_ID = 'selected-values';
const SELECTED_END_DROPPABLE_ID = 'selected-values:end';

function getAvailableItemId(option: string) {
  return `${AVAILABLE_ITEM_PREFIX}${option}`;
}

function getOptionFromAvailableItemId(id: string) {
  return id.slice(AVAILABLE_ITEM_PREFIX.length);
}

function isAvailableItemId(id: string) {
  return id.startsWith(AVAILABLE_ITEM_PREFIX);
}

interface DraggableStringItemProps {
  option: string;
  value: string;
  onAdd: () => void;
}

interface EndDropZoneProps {
  isActive: boolean;
  label: string;
}

function EndDropZone({ isActive, label }: EndDropZoneProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: SELECTED_END_DROPPABLE_ID,
  });

  return (
    <Box ref={setNodeRef} sx={{ minHeight: isActive ? 44 : 20 }}>
      {isActive && isOver ? (
        <DropPlaceholder label={label} />
      ) : (
        <Box
          sx={{
            height: '100%',
            minHeight: isActive ? 44 : 20,
            borderRadius: 1,
            border: isActive ? '1px dashed' : '1px solid transparent',
            borderColor: isActive ? 'primary.200' : 'transparent',
          }}
        />
      )}
    </Box>
  );
}

function DraggableStringItem({ option, value, onAdd }: DraggableStringItemProps) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: getAvailableItemId(option),
  });

  return (
    <Box ref={setNodeRef} {...attributes}>
      <StringItem
        value={value}
        onAdd={onAdd}
        isDragging={isDragging}
        showDragHandle
        listeners={listeners}
      />
    </Box>
  );
}

function SortableStringItem({ id, value, onRemove, rightContent }: SortableStringItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Box sx={style} ref={setNodeRef} {...attributes}>
      <StringItem
        value={value}
        onRemove={onRemove}
        isDragging={isDragging}
        showDragHandle
        listeners={listeners}
        rightContent={rightContent}
      />
    </Box>
  );
}

interface StringSelectorProps {
  availableOptions: string[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  availableLabel?: string;
  selectedLabel?: string;
  /** Optional: map value to display label (e.g. carousel id -> carousel title) */
  getOptionLabel?: (value: string) => string;
  /** Optional: transform value when adding (e.g. to generate unique ids for repeatable options) */
  getAddValue?: (option: string) => string;
  /** Optional: render content to the right of the item label (e.g. dropdown) */
  renderItemRightContent?: (value: string) => ReactNode;
}

function StringSelector({
  availableOptions,
  selectedValues,
  onChange,
  availableLabel = 'Available',
  selectedLabel = 'Selected',
  getOptionLabel,
  getAddValue,
  renderItemRightContent,
}: StringSelectorProps) {
  const displayValue = (value: string) => (getOptionLabel ? getOptionLabel(value) : value);
  const sensors = useSensors(useSensor(PointerSensor));
  const [activeItem, setActiveItem] = useState<string | null>(null);
  const [overItem, setOverItem] = useState<string | null>(null);
  const { setNodeRef: setSelectedDropRef, isOver: isOverSelected } = useDroppable({
    id: SELECTED_DROPPABLE_ID,
  });
  const collisionDetection: CollisionDetection = (args) => {
    const activeId = String(args.active.id);
    return isAvailableItemId(activeId) ? pointerWithin(args) : closestCenter(args);
  };

  const availableItems = availableOptions.filter((option) => {
    const valueToAdd = getAddValue ? getAddValue(option) : option;
    // Repeatable options (valueToAdd !== option) stay in available
    if (valueToAdd !== option) return true;
    return !selectedValues.includes(option);
  });

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    setActiveItem(active.id as string);
  };

  const handleDragOver = (event: DragOverEvent) => {
    setOverItem(event.over?.id ? String(event.over.id) : null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) {
      setActiveItem(null);
      setOverItem(null);
      return;
    }

    const activeId = active.id as string;
    const overId = over.id as string;

    if (isAvailableItemId(activeId)) {
      const option = getOptionFromAvailableItemId(activeId);
      const valueToAdd = getAddValue ? getAddValue(option) : option;
      const isRepeatable = valueToAdd !== option;

      if (!isRepeatable && selectedValues.includes(valueToAdd)) {
        setActiveItem(null);
        setOverItem(null);
        return;
      }

      const nextValues = [...selectedValues];
      const overIndex =
        overId === SELECTED_DROPPABLE_ID || overId === SELECTED_END_DROPPABLE_ID
          ? selectedValues.length
          : selectedValues.indexOf(overId);
      nextValues.splice(overIndex === -1 ? nextValues.length : overIndex, 0, valueToAdd);
      onChange(nextValues);
      setActiveItem(null);
      setOverItem(null);
      return;
    }

    if (activeId !== overId) {
      const oldIndex = selectedValues.indexOf(activeId);
      const newIndex = selectedValues.indexOf(overId);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newValues = arrayMove(selectedValues, oldIndex, newIndex);
        onChange(newValues);
      }
    }

    setActiveItem(null);
    setOverItem(null);
  };

  const handleAdd = (option: string) => {
    const valueToAdd = getAddValue ? getAddValue(option) : option;
    onChange([...selectedValues, valueToAdd]);
  };

  const handleRemove = (value: string) => {
    onChange(selectedValues.filter((v) => v !== value));
  };

  const isDraggingAvailableItem = !!activeItem && isAvailableItemId(activeItem);
  const activeOption = isDraggingAvailableItem ? getOptionFromAvailableItemId(activeItem) : null;
  const placeholderLabel = activeOption ? displayValue(activeOption) : '';
  const placeholderIndex =
    isDraggingAvailableItem && overItem
      ? overItem === SELECTED_DROPPABLE_ID
        ? selectedValues.length
        : overItem === SELECTED_END_DROPPABLE_ID
          ? selectedValues.length
          : selectedValues.indexOf(overItem)
      : -1;
  const shouldShowAppendPlaceholder =
    isDraggingAvailableItem &&
    overItem !== SELECTED_END_DROPPABLE_ID &&
    (placeholderIndex === selectedValues.length || (selectedValues.length === 0 && isOverSelected));

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <Stack direction="row" spacing={3}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
            {availableLabel}
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              minHeight: 200,
              bgcolor: 'grey.50',
            }}
          >
            <Stack spacing={1}>
              {availableItems.map((item) => (
                <DraggableStringItem
                  key={item}
                  option={item}
                  value={displayValue(item)}
                  onAdd={() => handleAdd(item)}
                />
              ))}
              {availableItems.length === 0 && (
                <Typography color="text.secondary" variant="body2">
                  No items available
                </Typography>
              )}
            </Stack>
          </Paper>
        </Box>

        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
            {selectedLabel}
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              minHeight: 200,
              bgcolor: isOverSelected ? 'primary.50' : 'grey.50',
              borderColor: isOverSelected ? 'primary.200' : 'divider',
            }}
            ref={setSelectedDropRef}
          >
            <SortableContext items={selectedValues} strategy={verticalListSortingStrategy}>
              <Stack spacing={1}>
                {selectedValues.map((item, index) => (
                  <React.Fragment key={item}>
                    {isDraggingAvailableItem && placeholderIndex === index && (
                      <DropPlaceholder label={placeholderLabel} />
                    )}
                    <SortableStringItem
                      id={item}
                      value={displayValue(item)}
                      onRemove={() => handleRemove(item)}
                      rightContent={renderItemRightContent?.(item)}
                    />
                  </React.Fragment>
                ))}
                {shouldShowAppendPlaceholder && <DropPlaceholder label={placeholderLabel} />}
                <EndDropZone isActive={isDraggingAvailableItem} label={placeholderLabel} />
                {selectedValues.length === 0 &&
                  !isDraggingAvailableItem &&
                  !shouldShowAppendPlaceholder && (
                    <Typography color="text.secondary" variant="body2">
                      Drag items here or click + to add
                    </Typography>
                  )}
              </Stack>
            </SortableContext>
          </Paper>
        </Box>
      </Stack>

      <DragOverlay>
        {activeItem ? (
          <StringItem
            value={displayValue(
              isAvailableItemId(activeItem) ? getOptionFromAvailableItemId(activeItem) : activeItem
            )}
            isDragging
            showDragHandle
            rightContent={
              isAvailableItemId(activeItem) ? null : renderItemRightContent?.(activeItem)
            }
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

export default StringSelector;
